import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { withHardware, withHole } from "../model/projectOps";
import type {
  Hardware,
  HardwareColor,
  HardwareSide,
  HardwareType,
} from "../model/Project";
import {
  HARDWARE_COLORS,
  HARDWARE_DEFAULT_SIZE_MM,
  holeRadiusForHardware,
} from "../hardware/HardwareCatalog";
import { hardwarePathData, shadeHex } from "../hardware/HardwareSymbols";
import {
  resolveDrawTarget,
  toOriginalSpace,
  type DrawTargetResolution,
} from "./drawTarget";
import { placementGuideRays } from "./placementGuides";

/**
 * Drops a piece of hardware (rivet / snap / magnetic snap / eyelet /
 * concho) onto a part. Hover shows a ghost of the cap so the user can
 * see exactly what will land where.
 *
 * Click rules — driven by what's under the cursor:
 *   1. Existing hole → replace whatever hardware is on it (or seat the
 *      first piece) with the currently-selected type / colour / size.
 *   2. Bare leather inside a part → drill a fresh hole sized for the
 *      hardware's post, then seat the hardware in it.
 *   3. Off any part → ignored.
 */
export class HardwareTool extends ToolBase {
  id = "hardware";
  label = "Hardware";
  shortcut = "r"; // mnemonic: "rivet"
  cursor = "crosshair";

  type: HardwareType = "rivet";
  color: HardwareColor = "silver";
  sizeMm: number = HARDWARE_DEFAULT_SIZE_MM["rivet"];
  /** Female (top / socket) vs male (bottom / stud). Only used when
   *  `type` is snap / magnetic-snap; ignored for one-piece hardware. */
  side: HardwareSide = "female";

  private ghost: paper.Item | null = null;
  private ghostKey: string | null = null;
  private lastCursor: { x: number; y: number } | null = null;

  setType(type: HardwareType) {
    this.type = type;
    // Snap the size to the new type's default if the current size isn't
    // useful for this type — keeps the picker from defaulting to a
    // wildly wrong number after a type swap.
    this.sizeMm = HARDWARE_DEFAULT_SIZE_MM[type];
    this.ghostKey = null; // force ghost rebuild on next hover
    if (this.lastCursor) this.refreshGhost(this.lastCursor);
  }

  setColor(color: HardwareColor) {
    this.color = color;
    this.ghostKey = null;
    if (this.lastCursor) this.refreshGhost(this.lastCursor);
  }

  setSizeMm(mm: number) {
    if (mm <= 0 || !Number.isFinite(mm)) return;
    this.sizeMm = mm;
    this.ghostKey = null;
    if (this.lastCursor) this.refreshGhost(this.lastCursor);
  }

  setSide(side: HardwareSide) {
    this.side = side;
    this.ghostKey = null;
    if (this.lastCursor) this.refreshGhost(this.lastCursor);
  }

  override onActivate() {
    // No subscribed settings to listen for — the hardware tool's state
    // (type, colour, size) lives on the tool itself, not on
    // ToolSettings, so it doesn't need an onSettingsChanged handler.
  }

  override onDeactivate() {
    this.clearGhost();
    this.ctx.scene.setGuideRays(null);
    this.lastCursor = null;
    setCanvasCursor(this.ctx.scene.getPaperScope(), "");
  }

  override onCancel() {
    this.clearGhost();
    this.ctx.scene.setGuideRays(null);
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    this.refreshGhost(p);
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    this.lastCursor = p;
    this.refreshGhost(p);
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);
    this.ctx.scene.setGuideRays(null);

    // Path 1 — existing hole under the cursor: just (re)seat the hardware.
    const hit = this.ctx.scene.hitTest(pt);
    if (hit.holeId && hit.pieceId) {
      const pieceId = hit.pieceId;
      const holeId = hit.holeId;
      this.ctx.commit("Place hardware", (proj) =>
        withHardware(proj, pieceId, this.makeHardware(holeId)),
      );
      return;
    }

    // Path 2 — bare leather inside a part: drill a hole sized for the
    // hardware's post, then seat the hardware on the new hole. Mirrors
    // HoleTool's "find a piece containing this point" lookup so we
    // accept clicks just inside the outline the same way it does.
    const target = this.findPieceContaining(scope, pt);
    if (!target) return;
    const { pieceId, offset } = target;
    // Store the drilled hole in the part's ORIGINAL space so the spread render
    // translate doesn't double-offset it (#786).
    const holePos = toOriginalSpace(p, offset);
    const newHoleId = newId("hole");
    const newHardwareId = newId("hw");
    const radius = holeRadiusForHardware(this.type, this.sizeMm);
    const hardware: Hardware = {
      id: newHardwareId,
      holeId: newHoleId,
      type: this.type,
      color: this.color,
      sizeMm: this.sizeMm,
      side: this.side,
    };
    this.ctx.commit("Place hardware", (proj) => {
      const withNewHole = withHole(proj, pieceId, {
        id: newHoleId,
        position: holePos,
        radius,
      });
      return withHardware(withNewHole, pieceId, hardware);
    });
  }

  /** Resolve the part whose outline contains `pt` (#812): never a hidden part,
   *  prefer the selected part, else the top-most visible part under the point.
   *  Containment-only — a hole must be drilled inside the outline, so no bbox
   *  fallback. */
  private findPieceContaining(
    scope: paper.PaperScope,
    pt: paper.Point,
  ): DrawTargetResolution | null {
    return resolveDrawTarget(
      this.ctx.history.state,
      { x: pt.x, y: pt.y },
      scope,
      this.ctx.scene.getSelection().pieceId,
      this.ctx.scene.getPartLayoutView(),
      null,
      { policy: "containment" },
    );
  }

  /** Build a fresh `Hardware` object from the tool's current settings,
   *  bound to the supplied `holeId`. */
  private makeHardware(holeId: string): Hardware {
    return {
      id: newId("hw"),
      holeId,
      type: this.type,
      color: this.color,
      sizeMm: this.sizeMm,
      side: this.side,
    };
  }

  /** Move / rebuild the ghost preview. Snaps to a hole's centre when the
   *  cursor is over one (so the user can see exactly where the hardware
   *  will seat); otherwise tracks the cursor (auto-drill case). */
  private refreshGhost(p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);
    const hit = this.ctx.scene.hitTest(pt);
    // If we're over no part at all, hide the ghost — there's nothing the
    // click would create.
    const overPiece =
      hit.pieceId || this.findPieceContaining(scope, pt)?.pieceId;
    setCanvasCursor(scope, overPiece ? "crosshair" : "default");
    if (!overPiece) {
      this.clearGhost();
      this.ctx.scene.setGuideRays(null);
      return;
    }
    const target = hit.holeId
      ? (this.holeCenter(hit.pieceId, hit.holeId, scope) ?? p)
      : p;
    this.ensureGhost(scope);
    if (this.ghost) {
      this.ghost.position = new scope.Point(target.x, target.y);
    }
    // Edge-distance + spacing guides (#919). When seating on an existing hole,
    // exclude it from the spacing search so it isn't measured against itself.
    const rays = placementGuideRays(
      this.ctx.history.state,
      target,
      overPiece,
      scope,
      this.ctx.scene.getPartLayoutView(),
      hit.holeId ? { excludeHoleId: hit.holeId } : undefined,
    );
    this.ctx.scene.setGuideRays(rays.length ? rays : null);
  }

  /** Look up the screen-space centre of an existing hole so the ghost
   *  snaps to it instead of floating at the cursor. */
  private holeCenter(
    pieceId: string | undefined,
    holeId: string,
    _scope: paper.PaperScope,
  ): { x: number; y: number } | null {
    if (!pieceId) return null;
    const piece = this.ctx.history.state.pieces.find((pc) => pc.id === pieceId);
    const hole = piece?.holes.find((h) => h.id === holeId);
    if (!hole) return null;
    return { x: hole.position.x, y: hole.position.y };
  }

  /** Lazy-build the ghost. Re-builds whenever the appearance key (type +
   *  colour + size + side) changes; otherwise we just translate the
   *  existing paper item, which is much cheaper than recreating it on
   *  every move. */
  private ensureGhost(scope: paper.PaperScope) {
    const key = `${this.type}|${this.color}|${this.sizeMm}|${this.side}`;
    if (this.ghost && this.ghostKey === key && this.ghost.parent) return;
    this.clearGhost();
    const fill = HARDWARE_COLORS[this.color];
    const rim = shadeHex(fill, -0.35);
    const path = new scope.CompoundPath({
      pathData: hardwarePathData(this.type, this.sizeMm, this.side),
    });
    path.fillColor = new scope.Color(fill);
    path.strokeColor = new scope.Color(rim);
    path.strokeWidth = 0.5;
    path.strokeScaling = false;
    path.opacity = 0.65;
    path.data = { ephemeral: true };
    scope.project.activeLayer?.addChild(path);
    this.ghost = path;
    this.ghostKey = key;
  }

  private clearGhost() {
    this.ghost?.remove();
    this.ghost = null;
    this.ghostKey = null;
  }
}

function setCanvasCursor(scope: paper.PaperScope, cursor: string) {
  const el = scope.view?.element as HTMLCanvasElement | undefined;
  if (el) el.style.cursor = cursor;
}
